/**
 * Vendor-agnostic analytics module for CareerForge.
 *
 * Design goals:
 *  - Zero external dependencies; SSR/typecheck-safe.
 *  - Single integration seam so the owner can drop in any vendor
 *    without touching call sites.
 *  - No-op in production until the owner wires a vendor; verbose in dev.
 *
 * --------------------------------------------------------------------------
 * HOW TO WIRE A VENDOR (what the owner must do):
 *
 *   Before your app boots (e.g. in index.tsx or a Vercel Analytics wrapper),
 *   set the global sink:
 *
 *     // TODO(owner): wire chosen vendor here (PostHog / GA4 / Plausible)
 *     // Example — PostHog:
 *     //   import posthog from "posthog-js";
 *     //   posthog.init("YOUR_API_KEY", { api_host: "https://app.posthog.com" });
 *     //   window.__analyticsSink = (event, props) => posthog.capture(event, props);
 *     //
 *     // Example — Plausible:
 *     //   window.__analyticsSink = (event, props) =>
 *     //     (window as any).plausible?.(event, { props });
 *     //
 *     // Example — GA4 (gtag):
 *     //   window.__analyticsSink = (event, props) =>
 *     //     (window as any).gtag?.("event", event, props);
 *
 * --------------------------------------------------------------------------
 */

// Top-level ESM import. These were previously `require("react")` inside each
// hook, commented "lazily so this file stays SSR-safe and tree-shakeable".
// Neither holds: there is no SSR here, every caller is a React hook (so React
// is already loaded), and an ESM import is MORE tree-shakeable than require.
// Under Vite it was fatal — no `require` in the browser, so /pricing threw
// ReferenceError at render and the money-gate E2E could never find its button.
import { useEffect, useRef } from "react";
import type { RefObject } from "react";

// ---------------------------------------------------------------------------
// Global sink type — declared on window for the owner integration seam
// ---------------------------------------------------------------------------

declare global {
  interface Window {
    /**
     * Pluggable analytics sink. Set this before the app boots to route all
     * `track()` calls into your chosen vendor (PostHog, GA4, Plausible, etc.).
     *
     * TODO(owner): wire chosen vendor here (PostHog / GA4 / Plausible)
     * — see PR description for copy-paste examples.
     */
    __analyticsSink?: (event: string, props?: Record<string, unknown>) => void;

    /**
     * Pluggable identity sink. Set alongside __analyticsSink to route
     * identify() calls into the vendor (e.g. posthog.identify).
     */
    __analyticsIdentify?: (userId: string, traits?: Record<string, unknown>) => void;

    /** Pluggable reset sink. Set alongside __analyticsSink (e.g. posthog.reset). */
    __analyticsReset?: () => void;
  }
}

// ---------------------------------------------------------------------------
// Core track() — the single call site API
// ---------------------------------------------------------------------------

// Events fired before the vendor sink is wired (the async-init window — the
// vendor SDK loads via dynamic import) are buffered here and replayed by
// flushPreInitEvents() once the sink is set. Without this, early events —
// notably a page_view fired on mount — are silently dropped because they race
// ahead of `await import("posthog-js")` completing. Verified in prod:
// page_view was landing zero capture requests until this buffer was added.
const preInitQueue: Array<[string, Record<string, unknown> | undefined]> = [];

/**
 * Fire an analytics event.
 *
 * In development: logs to console.debug so you can see events firing.
 * In production: dispatches to window.__analyticsSink if wired; otherwise the
 * event is buffered (see preInitQueue) and replayed once the sink initializes.
 *
 * @param event  Dot-namespaced event name, e.g. "pricing.page_view".
 * @param props  Flat key/value payload. Keep values JSON-serialisable.
 */
export function track(event: string, props?: Record<string, unknown>): void {
  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line no-console
    console.debug("[analytics]", event, props ?? {});
  }

  if (typeof window === "undefined") return;

  // TODO(owner): wire chosen vendor here (PostHog / GA4 / Plausible)
  // — see the window.__analyticsSink declaration above for examples.
  if (typeof window.__analyticsSink === "function") {
    window.__analyticsSink(event, props);
  } else {
    // Sink not wired yet — buffer instead of dropping the event.
    preInitQueue.push([event, props]);
  }
}

/**
 * Replay any events buffered before the sink was ready. The vendor-init module
 * MUST call this immediately after assigning window.__analyticsSink (see
 * initAnalytics.ts). Safe to call repeatedly and with an empty queue.
 */
export function flushPreInitEvents(): void {
  if (typeof window === "undefined" || typeof window.__analyticsSink !== "function") {
    return;
  }
  while (preInitQueue.length > 0) {
    const next = preInitQueue.shift();
    if (next) window.__analyticsSink(next[0], next[1]);
  }
}

// ---------------------------------------------------------------------------
// identify() / reset() — minimal identity plumbing for the vendor sink
// ---------------------------------------------------------------------------

/**
 * Associate subsequent events with a logged-in user. No-ops until the vendor
 * sink assigns window.__analyticsIdentify (see initAnalytics.ts).
 *
 * Traits are intentionally minimal — user id + plan tier only. Never pass
 * email or other PII here.
 */
export function identify(userId: string, traits: { plan_tier: string }): void {
  if (typeof window === "undefined") return;
  if (typeof window.__analyticsIdentify === "function") {
    window.__analyticsIdentify(userId, traits);
  }
}

/**
 * Clear the current identity (call on logout). No-ops until the vendor sink
 * assigns window.__analyticsReset (see initAnalytics.ts).
 */
export function reset(): void {
  if (typeof window === "undefined") return;
  if (typeof window.__analyticsReset === "function") {
    window.__analyticsReset();
  }
}

// ---------------------------------------------------------------------------
// useScrollDepth — fires scroll-depth milestones (25 / 50 / 75 / 100 %)
// ---------------------------------------------------------------------------

/**
 * Scroll depth tracking hook.
 *
 * Fires `track(eventName, { depth_pct })` at each milestone in
 * `milestones` (default: [25, 50, 75, 100]) — each milestone fires at most
 * once per mount. Works on document scroll (not element scroll).
 *
 * Usage:
 *   useScrollDepth("pricing.scroll_depth");
 *   useScrollDepth("pricing.scroll_depth", [33, 66, 100]);
 */
export function useScrollDepth(
  eventName: string,
  milestones: number[] = [25, 50, 75, 100],
): void {

  const firedRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    const fired = firedRef.current;

    function onScroll() {
      const scrollY = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;

      const pct = Math.round((scrollY / docHeight) * 100);

      for (const milestone of milestones) {
        if (!fired.has(milestone) && pct >= milestone) {
          fired.add(milestone);
          track(eventName, { depth_pct: milestone });
        }
      }
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [eventName, milestones]);
}

// ---------------------------------------------------------------------------
// useIntersectionTracker — fires once when an element enters the viewport
// ---------------------------------------------------------------------------

/**
 * Fires `track(eventName, extraProps)` once when the ref'd element becomes
 * visible (≥ threshold of its area intersecting). Used to mark "user reached
 * the All Access section" and to start a dwell timer.
 *
 * Returns: a ref to attach to the target element.
 *
 * Usage:
 *   const ref = useIntersectionTracker("pricing.all_access_seen");
 *   <Box ref={ref}>…All Access card…</Box>
 */
export function useIntersectionTracker(
  eventName: string,
  extraProps?: Record<string, unknown>,
  threshold = 0.25,
): RefObject<HTMLDivElement | null> {
  // eslint-disable-next-line @typescript-eslint/no-var-requires

  const ref = useRef<HTMLDivElement | null>(null);
  const firedRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !firedRef.current) {
            firedRef.current = true;
            track(eventName, extraProps);
            observer.disconnect();
          }
        }
      },
      { threshold },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [eventName, extraProps, threshold]);

  return ref;
}

// ---------------------------------------------------------------------------
// usePageTimer — measures dwell time from mount until unmount
// ---------------------------------------------------------------------------

/**
 * On unmount fires `track(eventName, { duration_sec })` with the number of
 * seconds the component was mounted. Useful for "time on /pricing" overall.
 *
 * Usage:
 *   usePageTimer("pricing.time_on_page");
 */
export function usePageTimer(eventName: string): void {
  // eslint-disable-next-line @typescript-eslint/no-var-requires

  const startRef = useRef<number>(Date.now());

  useEffect(() => {
    startRef.current = Date.now();
    return () => {
      const duration_sec = Math.round((Date.now() - startRef.current) / 1000);
      track(eventName, { duration_sec });
    };
  // eventName should be stable; intentionally omitted from dep array to
  // avoid retriggering the effect when the string reference changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

// ---------------------------------------------------------------------------
// useAllAccessDwellTimer — measures time from All Access becoming visible
// ---------------------------------------------------------------------------

/**
 * Starts a timer the first time `visible` flips true; fires
 * `track(eventName, { dwell_sec })` on page-hide / unmount.
 * Wraps the "time-on-page after the All Access section" requirement from §2a.
 *
 * Usage:
 *   const [allAccessVisible, setAllAccessVisible] = useState(false);
 *   useAllAccessDwellTimer("pricing.all_access_dwell", allAccessVisible);
 */
export function useAllAccessDwellTimer(
  eventName: string,
  visible: boolean,
): void {
  // eslint-disable-next-line @typescript-eslint/no-var-requires

  const startRef = useRef<number | null>(null);
  const firedRef = useRef(false);

  // Start the clock the first time the section becomes visible.
  useEffect(() => {
    if (visible && startRef.current === null) {
      startRef.current = Date.now();
    }
  }, [visible]);

  // Fire on unmount.
  useEffect(() => {
    return () => {
      if (startRef.current !== null && !firedRef.current) {
        firedRef.current = true;
        const dwell_sec = Math.round((Date.now() - startRef.current) / 1000);
        track(eventName, { dwell_sec });
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

// ---------------------------------------------------------------------------
// useExitTracking — fires on visibilitychange (hidden) and beforeunload
// ---------------------------------------------------------------------------

/**
 * Fires `track(eventName, { trigger })` when the user navigates away:
 *  - trigger: "visibility_hidden" — tab hidden / switched
 *  - trigger: "beforeunload"      — tab/window close or hard navigation
 *
 * Each trigger fires at most once per mount to avoid duplicate events when
 * both fire together (e.g. closing the tab triggers both in some browsers).
 *
 * Usage:
 *   useExitTracking("pricing.exit");
 */
export function useExitTracking(eventName: string): void {
  // eslint-disable-next-line @typescript-eslint/no-var-requires

  const firedRef = useRef(false);

  useEffect(() => {
    function fire(trigger: string) {
      if (!firedRef.current) {
        firedRef.current = true;
        track(eventName, { trigger });
      }
    }

    function onVisibility() {
      if (document.visibilityState === "hidden") {
        fire("visibility_hidden");
      }
    }

    function onBeforeUnload() {
      fire("beforeunload");
    }

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [eventName]);
}
